// Core
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Plugins
import { DASHBOARD_NULLISH_TEXT, formatNumber } from '../../plugins/general';
import { RequestMethod, requestVantageApi } from '../../plugins/request';

// Types
import { CustomerStats } from '../../types/global';
import { Theme } from '../../types/theme';

// Components
import {
  MapPin,
  Profile,
  Wifi,
} from '../../layout/icons';
import { Loader, LoaderSize } from '../../layout/elements/Loader';
import { OptionsDropdown } from '../../layout/elements/OptionsDropdown';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../layout/elements/Paragraph';
import { Tooltip, TooltipPlacement } from '../../layout/elements/Tooltip';

// Hooks
import { useOverflowTooltip } from '../../hooks/useOverflowTooltip/useOverflowTooltip';

// Component
import { CardCustomerProps } from './CardCustomer.types';
import {
  ActiveUsersContainer,
  CardCustomerComponent,
  ContentContainer,
  CustomerNameContainer,
  CustomerPreview,
  CustomerPreviewContainer,
  EquipmentUnitsContainer,
  MetricWrapper,
  MetricsContainer,
  OptionsDropdownContainer,
  SitesContainer,
} from './CardCustomer.style';

// Functional constants
const POLLING_INTERVAL_MS: number = 60000; // 1 minute
// @todo: Convert to WebSocket for real-time updates in the future

function CardCustomer({
  className,
  idCustomer,
  imgSrc,
  name,
  onClick,
  options,
  secondaryOptions,
  testId,
}: CardCustomerProps) {
  // Dependencies
  const theme: Theme = useTheme();
  const { t } = useTranslation();

  // Hooks
  const {
    containerRef: customerNameContainerRef,
    contentRef: customerNameContentRef,
    isTooltipVisible: renderCustomerNameTooltip,
    checkOverflowVisibility: updateCustomerNameTooltipRender,
  } = useOverflowTooltip({
    overflowCheckTriggers: name,
    widthOffset: 0,
  });

  /* ***********************************************************************************************
  *************************************** INITIAL STATES *******************************************
  *********************************************************************************************** */

  interface CardCustomerState {
    isLoading: boolean;
    stats: CustomerStats;
  }

  const initialState: CardCustomerState = {
    isLoading: false,
    stats: {
      alarms: null,
      equipmentUnits: null,
      equipmentUnitsOnline: null,
      sites: null,
      users: null,
    },
  };

  const [state, setState] = useState<CardCustomerState>(initialState);

  /* ***********************************************************************************************
  ****************************************** METHODS ***********************************************
  *********************************************************************************************** */

  /* ********** Request **********  */

  /* Fetch the customer stats by idCustomer */
  const fetchStats = useCallback(async (showLoading: boolean = false): Promise<void> => {
    setState((prevState: CardCustomerState): CardCustomerState => ({
      ...prevState,
      isLoading: showLoading,
    }));

    try {
      const stats: CustomerStats = await requestVantageApi.customer({
        method: RequestMethod.GET,
        path: '/customer/stats',
        params: { idCustomer },
      });

      setState((prevState: CardCustomerState): CardCustomerState => ({
        ...prevState,
        stats,
        isLoading: false,
      }));
    } catch (error: unknown) {
      // Clear stats on error to show DASHBOARD_NULLISH_TEXT instead of stale data
      setState((prevState: CardCustomerState): CardCustomerState => ({
        ...prevState,
        stats: {
          alarms: null,
          equipmentUnits: null,
          equipmentUnitsOnline: null,
          sites: null,
          users: null,
        },
        isLoading: false,
      }));
    }
  }, [idCustomer]);

  const renderCustomerName: JSX.Element = useMemo((): JSX.Element => (
    <CustomerNameContainer ref={customerNameContainerRef}>
      <ContentContainer ref={customerNameContentRef}>
        <Paragraph
          size={ParagraphSize.lg}
          weight={ParagraphWeight.bold}
          preventOverflow
        >
          {name}
        </Paragraph>
      </ContentContainer>
    </CustomerNameContainer>
  ), [name, customerNameContainerRef, customerNameContentRef]);

  /* ***********************************************************************************************
  ************************************** COMPONENT HANDLING ****************************************
  *********************************************************************************************** */

  useEffect((): () => void => {
    const resizeObserver: ResizeObserver = new ResizeObserver(() => {
      updateCustomerNameTooltipRender();
    });

    if (customerNameContentRef.current) {
      resizeObserver.observe(customerNameContentRef.current);
    }

    updateCustomerNameTooltipRender();

    return (): void => {
      resizeObserver.disconnect();
    };
  }, [updateCustomerNameTooltipRender, name, customerNameContentRef]);

  /* useEffect to fetch customer stats with polling every 1 minute */
  useEffect((): (() => void) => {
    // Fetch stats immediately on mount
    fetchStats(true);

    // Set up polling interval
    const intervalId: NodeJS.Timeout = setInterval((): void => {
      fetchStats();
    }, POLLING_INTERVAL_MS);

    // Cleanup: stop polling when component unmounts
    return (): void => {
      clearInterval(intervalId);
    };
  }, [fetchStats]);
  // @todo: Convert to WebSocket for real-time updates in the future

  return (
    <CardCustomerComponent
      data-testid={testId}
      className={className}
      onClick={onClick}
    >
      <OptionsDropdownContainer onClick={(e) => e.preventDefault()}>
        <OptionsDropdown options={options} secondaryOptions={secondaryOptions} />
      </OptionsDropdownContainer>
      <CustomerPreviewContainer>
        <CustomerPreview alt={name} src={imgSrc} />
      </CustomerPreviewContainer>
      {
        renderCustomerNameTooltip
          ? <Tooltip text={name}>{renderCustomerName}</Tooltip>
          : renderCustomerName
      }
      <MetricsContainer>
        <MetricWrapper>
          <Tooltip
            text={t('component.cardCustomer.equipmentUnitsOnlineTooltip', {
              online: state.stats.equipmentUnitsOnline ?? DASHBOARD_NULLISH_TEXT,
              total: state.stats.equipmentUnits ?? DASHBOARD_NULLISH_TEXT,
            })}
            placement={TooltipPlacement.right}
          >
            <EquipmentUnitsContainer data-testid="equipment-units-metric">
              <Wifi />
              {
                state.isLoading
                  ? <Loader size={LoaderSize.xs} />
                  : (
                    <Paragraph
                      size={ParagraphSize.sm}
                      color={theme.color.cardCustomer.metricsColor}
                      preventOverflow
                    >
                      {state.stats.equipmentUnitsOnline ?? DASHBOARD_NULLISH_TEXT}
                      /
                      {state.stats.equipmentUnits ?? DASHBOARD_NULLISH_TEXT}
                    </Paragraph>
                  )
              }
            </EquipmentUnitsContainer>
          </Tooltip>
        </MetricWrapper>
        <MetricWrapper>
          <Tooltip text={t('component.cardCustomer.sites')}>
            <SitesContainer>
              <MapPin />
              {
                state.isLoading
                  ? <Loader size={LoaderSize.xs} />
                  : (
                    <Paragraph
                      size={ParagraphSize.sm}
                      color={theme.color.cardCustomer.metricsColor}
                      preventOverflow
                    >
                      {formatNumber(state.stats.sites)}
                    </Paragraph>
                  )
              }
            </SitesContainer>
          </Tooltip>
        </MetricWrapper>
        <MetricWrapper>
          <Tooltip text={t('component.cardCustomer.activeUsers')}>
            <ActiveUsersContainer>
              <Profile />
              {
                state.isLoading
                  ? <Loader size={LoaderSize.xs} />
                  : (
                    <Paragraph
                      size={ParagraphSize.sm}
                      color={theme.color.cardCustomer.metricsColor}
                      preventOverflow
                    >
                      {formatNumber(state.stats.users)}
                    </Paragraph>
                  )
              }
            </ActiveUsersContainer>
          </Tooltip>
        </MetricWrapper>
      </MetricsContainer>
    </CardCustomerComponent>
  );
}

export { CardCustomer };
